import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react';
import { Badge, Button } from '@/components/ui';
import { ChainInventoryAnnotation } from '@/components/features/services/chain-inventory-annotation';
import type { FallbackChainEntry } from '@/types/service';
import type { IqServiceInventoryStatus } from '@/types/iq-service-inventory';

export interface ChainListProps {
  /** Aktuelle Chain-Eintraege, geordnet nach Prioritaet (Index 0 = Primaer). */
  value: FallbackChainEntry[];
  /** Fuegt einen neuen Eintrag hinzu — wird erst ab Phase 2 verdrahtet. */
  onAdd?: () => void;
  /** Bearbeitet den Eintrag an Position `index`. */
  onEdit?: (index: number) => void;
  /** Entfernt den Eintrag an Position `index`. */
  onRemove?: (index: number) => void;
  /** Verschiebt einen Eintrag (`from` -> `to`). */
  onReorder?: (from: number, to: number) => void;
  /** Read-Only-Modus (Phase 1 Default). Keine Aktions-Buttons sichtbar. */
  disabled?: boolean;
  /** Pro-Eintrag-Fehlermeldungen (z.B. Duplikat-Marker), Key = Index als String. */
  errors?: Record<string, string>;

  // --- Spec 347 §7: Routing<->Inventar-Verlinkung (alle optional, additiv) ---
  /** Inventar-Status je `gatewayServiceId` (status-ungefilterte Map). Fehlt der Key,
   *  ist die Kennung nicht (mehr) im geladenen Inventar — kein Badge. */
  inventoryStatusByKey?: Map<string, IqServiceInventoryStatus>;
  /** BFF-abgeleiteter Nutzungszaehler (Dimension a) je `gatewayServiceId`. */
  usageByKey?: Map<string, number>;
  /** Merchant des Service — Scope fuer den By-Key-Fallback der Status-Annotation. */
  merchantId?: string;
  /** Ist die status-ungefilterte Inventar-Abfrage fertig geladen? Steuert, ob der
   *  By-Key-Fallback fuer nicht-in-der-Map-enthaltene Keys feuern darf. */
  inventorySettled?: boolean;
  /** Darf der Aufrufer die Inventar-Nutzung ueberhaupt LESEN? (`IQ_SERVICE_INVENTORY_READ`,
   *  inkl. PM). Steuert, ob die Status-/Zaehler-Annotation (mit ihrer By-Key-Abfrage)
   *  ueberhaupt rendert — verhindert einen 403-Storm fuer Rollen ausserhalb des READ-Sets
   *  (merchant_it/merchant_viewer) auf den `ROLE_GROUPS.ALL`-Routing-Seiten. */
  canViewUsage?: boolean;
  /** Inventar-Abfrage degradiert → Annotation zeigt „nicht ermittelbar" statt nichts. */
  inventoryUnavailable?: boolean;
  /** Spec 352 §6: Inventar-Nutzung truncated → Zaehler „nicht ermittelbar" (Status bleibt). */
  inventoryTruncated?: boolean;
  /** Darf der Nutzer die platform-only Inventar-Seite oeffnen? (RBAC via hasRole im Parent). */
  canViewInventory?: boolean;
  /** Darf der Nutzer das platform-only Credential-Set oeffnen? */
  canViewCredential?: boolean;
  /** Deep-Link in das Merchant-Inventar (`?key=`). */
  onOpenInventoryKey?: (key: string) => void;
  /** Navigation zum Credential-Set (`/iq-services/{id}`). */
  onOpenCredential?: (iqServiceId: string) => void;
}

const MAX_CHAIN_ENTRIES = 5;

/**
 * Tabellarische Darstellung der FallbackChain (Spec 0216 §5.3.1).
 *
 * Vollstaendig "controlled" — kein internes State, alle Aktionen werden per
 * Callback an den Parent (RoutingPage) gemeldet. Phase 1 nutzt die
 * Component ausschliesslich im `disabled`-Modus (read-only Anzeige).
 *
 * Spec 347 §7: optionale Routing<->Inventar-Verlinkung — Status-Badge + (a)-Zaehler
 * neben der `gatewayServiceId`, klickbare IDs (gated via `canView*`). Badge/Zaehler
 * sind fuer alle Rollen sichtbar; nur die Navigation zu platform-only Seiten ist gegated.
 */
export function ChainList({
  value,
  onAdd,
  onEdit,
  onRemove,
  onReorder,
  disabled = false,
  errors,
  inventoryStatusByKey,
  usageByKey,
  merchantId,
  inventorySettled = false,
  canViewUsage = false,
  inventoryUnavailable = false,
  inventoryTruncated = false,
  canViewInventory = false,
  canViewCredential = false,
  onOpenInventoryKey,
  onOpenCredential,
}: ChainListProps) {
  const { t } = useTranslation('routing');
  const interactive = !disabled;
  const canAdd = interactive && !!onAdd && value.length < MAX_CHAIN_ENTRIES;

  if (value.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        <p>{t('chain.empty')}</p>
        {interactive && onAdd && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => onAdd()}
          >
            {t('chain.addFirst')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{t('table.header.priority')}</th>
              <th className="px-3 py-2 text-left">{t('table.header.flowType')}</th>
              <th className="px-3 py-2 text-left">{t('table.header.gatewayServiceId')}</th>
              <th className="px-3 py-2 text-left">{t('table.header.iqOverride')}</th>
              {interactive && (
                <th className="px-3 py-2 text-right">{t('table.header.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {value.map((entry, index) => {
              const isPrimary = index === 0;
              const isLast = index === value.length - 1;
              const errorKey = String(index);
              const entryError = errors?.[errorKey];

              const gatewayClickable = canViewInventory && !!onOpenInventoryKey;
              const credentialClickable = canViewCredential && !!onOpenCredential;

              return (
                <tr
                  key={`${entry.gatewayServiceId}-${index}`}
                  className={`border-t ${isPrimary ? 'bg-primary/5' : ''}`}
                  data-testid={`chain-row-${index}`}
                >
                  <td className="px-3 py-2 align-top">
                    {/*
                      Spec 0216 UX-Followup 2026-05-20:
                      Primaer-Badge entfernt — Position #1 + Hint-Text
                      "Ordered by priority — entry 1 is the primary flow"
                      (Tabellen-Header) genuegen. Badge fuehrte zu visuell
                      verschobenem Layout bei mehreren Eintraegen (#2+ ohne Badge
                      ergab andere Spaltenbreite).
                    */}
                    <span className="font-medium">#{index + 1}</span>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Badge variant="outline">{entry.flowType}</Badge>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      {gatewayClickable ? (
                        <button
                          type="button"
                          className="font-mono text-xs text-primary hover:underline"
                          onClick={() => onOpenInventoryKey!(entry.gatewayServiceId)}
                          aria-label={t('chain.viewInInventory', { key: entry.gatewayServiceId })}
                        >
                          {entry.gatewayServiceId}
                        </button>
                      ) : (
                        <span className="font-mono text-xs">{entry.gatewayServiceId}</span>
                      )}
                      {/* Annotation nur fuer READ-faehige Aufrufer — sonst feuert ihre
                          By-Key-Abfrage 403s (merchant_it/viewer sind nicht im READ-Set). */}
                      {canViewUsage && (
                        <ChainInventoryAnnotation
                          merchantId={merchantId}
                          gatewayServiceId={entry.gatewayServiceId}
                          statusFromMap={inventoryStatusByKey?.get(entry.gatewayServiceId)}
                          usageFromMap={usageByKey?.get(entry.gatewayServiceId)}
                          mapSettled={inventorySettled}
                          mapUnavailable={inventoryUnavailable}
                          mapTruncated={inventoryTruncated}
                        />
                      )}
                    </div>
                    {entryError && (
                      <p className="mt-1 text-xs text-destructive">{entryError}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {entry.iqServiceId ? (
                      credentialClickable ? (
                        <button
                          type="button"
                          className="font-mono text-xs text-primary hover:underline"
                          onClick={() => onOpenCredential!(entry.iqServiceId!)}
                          aria-label={t('chain.viewCredential', { id: entry.iqServiceId })}
                        >
                          {entry.iqServiceId}
                        </button>
                      ) : (
                        <span className="font-mono text-xs">{entry.iqServiceId}</span>
                      )
                    ) : (
                      <span className="text-xs italic text-muted-foreground">
                        {t('chain.iqOverrideNone')}
                      </span>
                    )}
                  </td>
                  {interactive && (
                    <td className="px-3 py-2 align-top">
                      <div className="flex justify-end gap-1">
                        {onEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 coarse:min-h-11 coarse:min-w-11"
                            onClick={() => onEdit(index)}
                            aria-label={t('chain.action.edit', { index: index + 1 })}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {onReorder && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 coarse:min-h-11 coarse:min-w-11"
                              onClick={() => onReorder(index, index - 1)}
                              disabled={isPrimary}
                              aria-label={t('chain.action.moveUp', { index: index + 1 })}
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 coarse:min-h-11 coarse:min-w-11"
                              onClick={() => onReorder(index, index + 1)}
                              disabled={isLast}
                              aria-label={t('chain.action.moveDown', { index: index + 1 })}
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {onRemove && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive coarse:min-h-11 coarse:min-w-11"
                            onClick={() => onRemove(index)}
                            aria-label={t('chain.action.remove', { index: index + 1 })}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {interactive && onAdd && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {t('chain.countHint', { count: value.length, max: MAX_CHAIN_ENTRIES })}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAdd()}
            disabled={!canAdd}
            title={!canAdd ? t('chain.maxReachedTooltip') : undefined}
          >
            {t('chain.addEntry')}
          </Button>
        </div>
      )}
    </div>
  );
}
